"use client";

import { useEffect, useState, ReactNode } from "react";

import { ThemeProvider } from "@jetspeak/jetclass-ui-lib";
import { QueryClientProvider } from "@tanstack/react-query";
import i18n from "i18next";
import { usePathname } from "next/navigation";
import { AppProgressBar as ProgressBar } from "next-nprogress-bar";

import { createQueryClient } from "@packages/data/api";
import { getCurrentLocale } from "@packages/shared/utils/getCurrentLocale";
import { RootStore, RootStoreProvider, setupRootStore } from "@packages/store";

import { AuthGuard } from "@/components/AuthGuard";
import { authRoutes } from "@/constants";
import { currentLang, initI18n } from "@/services/i18n";
import { userHelpers } from "@/utils/helpers/userHelpers";

import "@jetspeak/jetclass-ui-lib/style.css";

const query = createQueryClient();

const handleUnauthorizedError = async (store: RootStore, pathname: string) => {
  if (userHelpers.isAnyRouteMatch(authRoutes, pathname)) {
    return;
  }

  await store.auth.logout();

  location.reload();
};

interface AppProps {
  children: ReactNode;
}

export default function App({ children }: AppProps) {
  const [rootStore, setRootStore] = useState<RootStore>();

  const [themeLocale, setThemeLocale] = useState(getCurrentLocale());

  const pathname = usePathname();

  useEffect(() => {
    initI18n();

    const appLanguage = currentLang();

    setupRootStore({ appLanguage }).then((store) => {
      store.auth.onUnauthorizedError(
        handleUnauthorizedError.bind(null, store, pathname)
      );

      store.auth.getCurrentUser();

      setRootStore(store);
    });

    query.changeLanguage(appLanguage);
    document.documentElement.lang = appLanguage;
    setThemeLocale(getCurrentLocale());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!rootStore) return;

    const handleLanguageChanged = (lng: string) => {
      rootStore.setAppLanguage(lng);
      query.changeLanguage(lng);
      document.documentElement.lang = lng;
      setThemeLocale(getCurrentLocale());
    };

    rootStore.auth.onUnauthorizedError(
      handleUnauthorizedError.bind(null, rootStore, pathname)
    );
    query.onUnauthorizedError(
      handleUnauthorizedError.bind(null, rootStore, pathname)
    );
    i18n.on("languageChanged", handleLanguageChanged);

    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, [pathname, rootStore]);

  if (!rootStore) {
    return null;
  }

  return (
    <RootStoreProvider value={rootStore}>
      <ThemeProvider theme="govoriru" locale={themeLocale}>
        <QueryClientProvider client={query.queryClient}>
          <ProgressBar color="#ffa007" options={{ showSpinner: true }} />
          <AuthGuard />
          {children}
        </QueryClientProvider>
      </ThemeProvider>
    </RootStoreProvider>
  );
}
